import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge } from "react-bootstrap";
import { FaFilePdf, FaCheck } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { withRouter } from "../../common/with-router";
import { withTranslation } from "react-i18next";
import { formataDataHoraExibicao, formataTelefoneExibicao } from "../../common/utils";
import { SeletorIdiomaCompacto } from "../../common/_components/SeletorIdioma";

const MySwal = withReactContent(Swal);

/**
 * Assinatura da declaração de indicação pelo médico, por link aberto — sem login.
 *
 * A navegadora preenche os dados do profissional indicado e envia este link para o médico
 * apenas assinar. É o caminho principal, e não a exceção: a maioria dos médicos da Etapa 1
 * não tem acesso à plataforma.
 *
 * Reaberto depois de assinada, o link mostra o documento assinado em vez do formulário —
 * ninguém assina duas vezes, e quem guardou o e-mail continua conseguindo ver o que assinou.
 */
class AssinaturaIndicacao extends Component {
    constructor(props) {
        super(props);
        this.state = {
            carregando: true,
            erro: "",
            dados: null,
            autoriza_contato: null,
            assino: false,
            erros: {},
            salvando: false,
        };
    }

    componentDidMount() {
        this.carregar();
    }

    uuid = () => this.props.router?.params?.uuid || "";

    carregar = async () => {
        try {
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/assinatura/${this.uuid()}/`, {
                headers: { "X-USERNAME": import.meta.env.VITE_X_USERNAME },
            });
            this.setState({ carregando: false, dados: data, autoriza_contato: data.autoriza_contato });
        } catch (error) {
            const detalhe = error.response?.data?.detail;
            this.setState({
                carregando: false,
                // o detalhe vem da API; sem ele, a mensagem padrão no idioma da tela
                erro: detalhe || this.props.t("indicacoes:assinatura.errors.open"),
            });
        }
    };

    assinar = async (e) => {
        e.preventDefault();
        const { t } = this.props;
        // chaves, traduzidas no render: acompanham a troca de idioma
        const erros = {};
        if (this.state.autoriza_contato === null) erros.autoriza_contato = "indicacoes:assinatura.errors.authorization";
        if (!this.state.assino) erros.assino = "indicacoes:assinatura.errors.confirm";
        this.setState({ erros });
        if (Object.keys(erros).length) return;

        this.setState({ salvando: true });
        try {
            await api.post(
                `/pessoas/indicacoes-profissionais/assinatura/${this.uuid()}/`,
                { autoriza_contato: this.state.autoriza_contato },
                { headers: { "X-USERNAME": import.meta.env.VITE_X_USERNAME } }
            );
            await MySwal.fire({
                icon: "success",
                title: t("indicacoes:assinatura.signedTitle"),
                text: t("indicacoes:assinatura.signedText"),
                confirmButtonText: t("indicacoes:assinatura.understood"),
            });
            this.setState({ salvando: false });
            await this.carregar();
        } catch (error) {
            console.error("Erro ao assinar a declaração:", error);
            this.setState({ salvando: false });
            const dados = error.response?.data || {};
            MySwal.fire({
                icon: "error", title: t("common:alert.attention"), showCloseButton: true,
                text: dados.detail || Object.values(dados).flat()[0] ||
                      t("indicacoes:assinatura.errors.sign"),
            });
        }
    };

    renderDados = () => {
        const { t } = this.props;
        const { dados } = this.state;
        const conselho = [dados.indicado.conselho, dados.indicado.registro, dados.indicado.registro_uf]
            .filter(Boolean).join(" ") || "-";
        const crm = [dados.medico.registro, dados.medico.registro_uf].filter(Boolean).join("-") || "-";

        return (
            <Table responsive className="custom-table mb-4">
                <tbody>
                    <tr><th style={{ width: "38%" }}>{t("indicacoes:assinatura.table.doctor")}</th><td>{t("indicacoes:assinatura.table.doctorValue", { nome: dados.medico.nome || "-", crm })}</td></tr>
                    <tr><th>{t("indicacoes:assinatura.table.referred")}</th><td>{dados.indicado.nome || "-"}</td></tr>
                    <tr><th>{t("indicacoes:assinatura.table.council")}</th><td>{conselho}</td></tr>
                    <tr><th>{t("indicacoes:assinatura.table.phone")}</th><td>{formataTelefoneExibicao(dados.indicado.telefone) || "-"}</td></tr>
                    <tr><th>{t("indicacoes:assinatura.table.email")}</th><td>{dados.indicado.email || "-"}</td></tr>
                    <tr><th>{t("indicacoes:assinatura.table.program")}</th><td>{dados.programa.nome || "-"}</td></tr>
                </tbody>
            </Table>
        );
    };

    /** Já assinada: mostra o documento, não o formulário. */
    renderAssinada = () => {
        const { t } = this.props;
        const { dados } = this.state;
        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <h5 className="mb-0">{t("indicacoes:assinatura.declaration")}</h5>
                        <Badge bg="success">
                            {t("indicacoes:assinatura.signedAt", { data: dados.data_assinatura ? formataDataHoraExibicao(dados.data_assinatura) : "-" })}
                        </Badge>
                    </div>
                    {this.renderDados()}
                    <p className="text-muted">
                        {t("indicacoes:assinatura.alreadySigned")}
                    </p>
                    {dados.comprovante_url && (
                        <Button variant="success" href={dados.comprovante_url} target="_blank" rel="noopener noreferrer">
                            <FaFilePdf className="me-1" /> {t("indicacoes:assinatura.viewSigned")}
                        </Button>
                    )}
                </Card.Body>
            </Card>
        );
    };

    renderFormulario = () => {
        const { t } = this.props;
        const { dados, autoriza_contato, assino, erros, salvando } = this.state;

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h5>{t("indicacoes:assinatura.formTitle")}</h5>
                    <p className="text-muted">
                        {t("indicacoes:assinatura.formIntro")}
                    </p>

                    {this.renderDados()}

                    <Form onSubmit={this.assinar}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                {t("indicacoes:assinatura.authorizationText")}
                            </Form.Label>
                            <div>
                                {[["sim", true], ["nao", false]].map(([chave, valor]) => (
                                    <Form.Check inline type="radio" key={chave} id={`autoriza_${chave}`}
                                                name="autoriza_contato"
                                                label={valor ? t("indicacoes:assinatura.yes") : t("indicacoes:assinatura.no")}
                                                checked={autoriza_contato === valor}
                                                onChange={() => this.setState({
                                                    autoriza_contato: valor,
                                                    erros: { ...erros, autoriza_contato: "" },
                                                })} />
                                ))}
                            </div>
                            {erros.autoriza_contato && (
                                <div className="text-danger small mt-1">{t(erros.autoriza_contato)}</div>
                            )}
                        </Form.Group>

                        <Alert variant="light" className="border">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <div>
                                    <strong>{t("indicacoes:assinatura.declarationTitle")}</strong>
                                    <div className="text-muted small">
                                        {t("indicacoes:assinatura.declarationHint")}
                                    </div>
                                </div>
                                {dados.declaracao_url && (
                                    <Button variant="outline-secondary" size="sm" href={dados.declaracao_url}
                                            target="_blank" rel="noopener noreferrer">
                                        <FaFilePdf className="me-1" /> {t("indicacoes:assinatura.readDeclaration")}
                                    </Button>
                                )}
                            </div>

                            <Form.Check
                                type="checkbox" id="assino_declaracao" className="mt-3"
                                label={t("indicacoes:assinatura.signCheckbox")}
                                checked={assino}
                                onChange={() => this.setState({ assino: !assino, erros: { ...erros, assino: "" } })}
                            />
                            {erros.assino && <div className="text-danger small mt-1">{t(erros.assino)}</div>}
                        </Alert>

                        <Button variant="success" type="submit" className="btn-submit" disabled={salvando}>
                            {salvando ? <span className="spinner-border spinner-border-sm"></span>
                                      : <><FaCheck className="me-1" /> {t("indicacoes:assinatura.sign")}</>}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    render() {
        const { t } = this.props;
        const { carregando, erro, dados } = this.state;

        return (
            <Row className="justify-content-center">
                {/* link aberto do e-mail, sem login: o seletor compacto é a forma de trocar o idioma */}
                <SeletorIdiomaCompacto />
                <Col lg={9} xl={8}>
                    {/* a logo identifica o programa, como nas telas de termo: o médico abre
                        este link direto do e-mail, sem o resto do sistema em volta */}
                    {dados?.programa?.imagem_branca && (
                        <div className="mb-3 mt-2 text-center">
                            <img src={dados.programa.imagem_branca} alt={dados.programa.nome}
                                 style={{ maxWidth: "200px" }} />
                        </div>
                    )}
                    {carregando && (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    )}
                    {!carregando && erro && <Alert variant="danger">{erro}</Alert>}
                    {!carregando && !erro && dados && dados.cancelada && (
                        <Alert variant="warning">
                            {t("indicacoes:assinatura.closed")}
                        </Alert>
                    )}
                    {!carregando && !erro && dados && !dados.cancelada && (
                        dados.assinada ? this.renderAssinada() : this.renderFormulario()
                    )}
                </Col>
            </Row>
        );
    }
}

AssinaturaIndicacao.propTypes = {
    t: PropTypes.func.isRequired,
    router: PropTypes.object,
};

export default withTranslation(["indicacoes", "common"])(withRouter(AssinaturaIndicacao));
